/* global SideDrawer */

const CartEnhancements = (Base) => class extends Base {
  bindCartEnhancementEvents() {
    if (this.cartEnhancementsBound) return;

    this.popupClickHandler = this.handlePopupClick.bind(this);
    this.popupKeyupHandler = this.handlePopupKeyup.bind(this);
    this.popupCartAddHandler = this.popupCartAddHandler || (() => this.closeAllUpsellPopups(false));
    this.cartAfterMergeHandler = this.handleCartAfterMerge.bind(this);

    this.addEventListener('click', this.popupClickHandler);
    this.addEventListener('keyup', this.popupKeyupHandler);
    this.addEventListener('on:cart:add', this.popupCartAddHandler);
    this.addEventListener('on:cart:after-merge', this.cartAfterMergeHandler);

    this.cartEnhancementsBound = true;
  }

  unbindCartEnhancementEvents() {
    if (!this.cartEnhancementsBound) return;

    this.removeEventListener('click', this.popupClickHandler);
    this.removeEventListener('keyup', this.popupKeyupHandler);
    this.removeEventListener('on:cart:add', this.popupCartAddHandler);
    this.removeEventListener('on:cart:after-merge', this.cartAfterMergeHandler);

    this.cartEnhancementsBound = false;
  }

  handleCartAfterMerge() {
    this.initCartDynamicContent();
  }

  initCartDynamicContent() {
    theme.manuallyLoadImages(this);
    this.querySelectorAll('cc-cart-cross-sell').forEach((el) => el.init());
    this.querySelectorAll('carousel-slider').forEach((el) => el.refresh());
  }

  /**
   * Handles upsell option popup clicks.
   * @param {object} evt - Event object.
   */
  handlePopupClick(evt) {
    const cartItemVariantSubmit = evt.target.closest('[data-cart-item-variant-submit]');
    if (cartItemVariantSubmit) {
      this.changeCartItemVariant(cartItemVariantSubmit);
      return;
    }

    const openBtn = evt.target.closest('[data-cart-upsell-popup-open]');
    if (openBtn) {
      const popup = document.getElementById(openBtn.getAttribute('aria-controls'));
      if (popup) this.openUpsellPopup(popup, openBtn);
      return;
    }

    const closeBtn = evt.target.closest('[data-cart-upsell-popup-close]');
    if (closeBtn) {
      const popup = closeBtn.closest('.cart-drawer-popup');
      if (popup) this.closeUpsellPopup(popup);
    }
  }

  /**
   * Handles Escape inside an upsell option popup.
   * @param {object} evt - Event object.
   */
  handlePopupKeyup(evt) {
    if (evt.key !== 'Escape') return;
    const popup = evt.target.closest('.cart-drawer-popup');
    if (popup) this.closeUpsellPopup(popup);
  }

  /**
   * Opens an upsell option popup.
   * @param {Element} popup - Popup element.
   * @param {Element} opener - Button that opened the popup.
   */
  openUpsellPopup(popup, opener) {
    this.querySelectorAll('.cart-drawer-popup:not([hidden])').forEach((el) => {
      if (el !== popup) this.closeUpsellPopup(el);
    });

    popup.hidden = false;
    popup.setAttribute('aria-hidden', 'false');
    popup.opener = opener;

    const firstOption = popup.querySelector('.js-option:not(:disabled)');
    const closeBtn = popup.querySelector('[data-cart-upsell-popup-close]');
    setTimeout(() => (firstOption || closeBtn || popup).focus(), 0);
  }

  /**
   * Changes a cart line item to another variant of the same product.
   * @param {Element} button - Confirmation button.
   */
  async changeCartItemVariant(button) {
    if (button.getAttribute('aria-disabled') === 'true') return;

    const form = button.closest('[data-cart-item-variant-form]');
    if (!form) return;

    const idInput = form.querySelector('[name="id"]');
    if (!form.reportValidity() || !idInput.value) return;

    const newVariantId = parseInt(idInput.value, 10);
    const currentVariantId = parseInt(form.dataset.currentVariantId, 10);
    const popup = button.closest('.cart-drawer-popup');

    if (newVariantId === currentVariantId) {
      if (popup) this.closeUpsellPopup(popup);
      return;
    }

    const errorEl = form.querySelector('.js-form-error');
    if (errorEl) {
      errorEl.hidden = true;
      errorEl.textContent = '';
    }

    button.setAttribute('aria-disabled', 'true');
    button.classList.add('is-loading');

    const quantity = parseInt(form.dataset.lineQuantity || 1, 10);
    const variantPicker = form.querySelector('variant-picker');
    const quantityLimit = variantPicker && typeof variantPicker.getQuantityLimit === 'function'
      ? variantPicker.getQuantityLimit()
      : null;
    const addPayload = {
      id: newVariantId,
      quantity: quantityLimit ? Math.min(quantity, quantityLimit) : quantity
    };
    const originalPayload = {
      id: currentVariantId,
      quantity
    };
    const sellingPlanInput = form.querySelector('[name="selling_plan"]');
    if (sellingPlanInput && sellingPlanInput.value) {
      addPayload.selling_plan = sellingPlanInput.value;
      originalPayload.selling_plan = sellingPlanInput.value;
    }

    const propertiesEl = form.querySelector('[data-cart-item-properties]');
    if (propertiesEl) {
      const propertiesData = JSON.parse(propertiesEl.textContent || '{}');
      const properties = Array.isArray(propertiesData)
        ? propertiesData.reduce((acc, property) => {
          const name = Array.isArray(property) ? property[0] : property.name;
          const value = Array.isArray(property) ? property[1] : property.value;
          if (name && value !== null && typeof value !== 'undefined' && value !== '') acc[name] = value;
          return acc;
        }, {})
        : propertiesData;

      if (properties && Object.keys(properties).length) {
        addPayload.properties = properties;
        originalPayload.properties = properties;
      }
    }

    const jsonFetch = async (url, payload) => {
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
          'X-Requested-With': 'XMLHttpRequest'
        },
        body: JSON.stringify(payload)
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.description || data.message || response.status);
      return data;
    };
    const getCart = async () => {
      const response = await fetch(`${theme.routes.cart}.js`, {
        headers: {
          Accept: 'application/json',
          'X-Requested-With': 'XMLHttpRequest'
        }
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.description || data.message || response.status);
      return data;
    };
    const getCartVariantQuantity = (cart, variantId) => (
      cart && cart.items
        ? cart.items.reduce(
          (total, item) => (item.variant_id === variantId ? total + item.quantity : total),
          0
        )
        : 0
    );

    try {
      const cartBeforeChange = await getCart().catch(() => null);
      const newVariantQuantityBefore = getCartVariantQuantity(cartBeforeChange, newVariantId);
      if (quantityLimit) {
        addPayload.quantity = Math.min(
          quantity,
          Math.max(quantityLimit - newVariantQuantityBefore, 0)
        );
      }
      if (addPayload.quantity < 1) throw new Error(theme.strings.noStock);

      await jsonFetch(`${theme.routes.cart}/change.js`, {
        id: form.dataset.lineKey,
        quantity: 0
      });

      try {
        await jsonFetch(theme.routes.cartAdd, addPayload);
      } catch (addError) {
        const cartAfterAdd = await getCart().catch(() => null);
        if (getCartVariantQuantity(cartAfterAdd, newVariantId) > newVariantQuantityBefore) {
          this.closeAllUpsellPopups(false);
          document.dispatchEvent(
            new CustomEvent('on:cart:change', { bubbles: true, cancelable: false })
          );
          return;
        }

        await jsonFetch(theme.routes.cartAdd, originalPayload).catch(() => null);
        document.dispatchEvent(
          new CustomEvent('on:cart:change', { bubbles: true, cancelable: false })
        );
        throw addError;
      }

      this.closeAllUpsellPopups(false);
      document.dispatchEvent(
        new CustomEvent('on:cart:change', { bubbles: true, cancelable: false })
      );
    } catch (error) {
      if (errorEl) {
        errorEl.hidden = false;
        errorEl.textContent = error.message;
      }
      console.log(error); // eslint-disable-line
    } finally {
      button.classList.remove('is-loading');
      button.removeAttribute('aria-disabled');
    }
  }

  /**
   * Closes an upsell option popup.
   * @param {Element} popup - Popup element.
   */
  closeUpsellPopup(popup, restoreFocus = true) {
    popup.hidden = true;
    popup.setAttribute('aria-hidden', 'true');
    if (restoreFocus && popup.opener) popup.opener.focus();
  }

  /**
   * Closes all upsell option popups.
   * @param {boolean} restoreFocus - Restore focus to each opener.
   */
  closeAllUpsellPopups(restoreFocus = true) {
    this.querySelectorAll('.cart-drawer-popup:not([hidden])').forEach((popup) => {
      this.closeUpsellPopup(popup, restoreFocus);
    });
  }
};

class CartDrawer extends CartEnhancements(SideDrawer) {
  connectedCallback() {
    this.bindEvents();
    this.bindCartEnhancementEvents();
  }

  bindEvents() {
    if (this.drawerEventsBound) return;

    this.openDrawerViaEventHandler = this.handleDrawerOpenViaEvent.bind(this);
    this.closeDrawerViaEventHandler = this.close.bind(this, null);
    this.cartRefreshEventHandler = this.cartRefreshHandler.bind(this);
    this.beforeOpenHandler = this.initCartDynamicContent.bind(this);

    document.addEventListener('dispatch:cart-drawer:open', this.openDrawerViaEventHandler);
    document.addEventListener('dispatch:cart-drawer:close', this.closeDrawerViaEventHandler);
    document.addEventListener('dispatch:cart-drawer:refresh', this.cartRefreshEventHandler);
    this.addEventListener('on:cart-drawer:before-open', this.beforeOpenHandler);

    this.drawerEventsBound = true;
  }

  disconnectedCallback() {
    if (!this.drawerEventsBound) {
      this.unbindCartEnhancementEvents();
      return;
    }

    document.removeEventListener('dispatch:cart-drawer:refresh', this.cartRefreshEventHandler);
    document.removeEventListener('dispatch:cart-drawer:open', this.openDrawerViaEventHandler);
    document.removeEventListener('dispatch:cart-drawer:close', this.closeDrawerViaEventHandler);
    this.removeEventListener('on:cart-drawer:before-open', this.beforeOpenHandler);
    this.drawerEventsBound = false;
    this.unbindCartEnhancementEvents();
  }

  /**
   * Handle when the drawer is opened via an event
   * @param {object} evt - Event object.
   */
  handleDrawerOpenViaEvent(evt) {
    this.open(evt.detail ? evt.detail.opener : null);
  }

  /**
   * Trigger refresh of contents
   */
  cartRefreshHandler() {
    this.querySelector('cart-form').refresh();
  }

  /**
   * Update section's cart-form element with new contents
   * @param {string} html - Whole-section HTML.
   */
  updateFromCartChange(html) {
    this.querySelector('cart-form').refreshFromHtml(html);
  }
}

class CartPage extends CartEnhancements(HTMLElement) {
  connectedCallback() {
    this.bindCartEnhancementEvents();
    this.initCartDynamicContent();
  }

  disconnectedCallback() {
    this.unbindCartEnhancementEvents();
  }
}

if (!window.customElements.get('cart-drawer')) {
  window.customElements.define('cart-drawer', CartDrawer);
}

if (!window.customElements.get('cart-page')) {
  window.customElements.define('cart-page', CartPage);
}
